import authService from './authService';
import {
  InvoiceTransactionType,
  InvoiceTransactionTypeWithERP,
  InvoiceTransactionTypeFormData,
  InvoiceTransactionTypeSearchModel,
} from '../types/invoiceTransactionType';
import { PaginatedResponse, ApiResponse } from '../types/common';

class InvoiceTransactionTypeService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /**
   * Lista naturezas de operação com paginação
   * GET /api/invoice-transaction-types
   */
  async getAll(
    searchModel: InvoiceTransactionTypeSearchModel,
  ): Promise<PaginatedResponse<InvoiceTransactionType>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      page_size: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { q: searchModel.searchName }),
      ...(searchModel.orderBy && { order_by: searchModel.orderBy }),
      ...(searchModel.orderDirection && { order_direction: searchModel.orderDirection }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/invoice-transaction-types?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar naturezas de operação');
    }

    const json = await response.json();
    const items = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? searchModel.pageSize,
        total: pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  /**
   * Busca uma natureza de operação por ID (com mapeamentos ERP)
   * GET /api/invoice-transaction-types/:id
   */
  async getById(id: number): Promise<InvoiceTransactionTypeWithERP> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/invoice-transaction-types/${id}`);

    if (!response.ok) {
      if (response.status === 404) throw new Error('Natureza de operação não encontrada');
      throw new Error('Falha ao buscar natureza de operação');
    }

    const data: ApiResponse<InvoiceTransactionTypeWithERP> = await response.json();
    return data.data!;
  }

  /**
   * Cria uma nova natureza de operação com mapeamentos ERP
   * POST /api/invoice-transaction-types
   */
  async create(formData: InvoiceTransactionTypeFormData): Promise<InvoiceTransactionTypeWithERP> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/invoice-transaction-types', formData);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Falha ao criar natureza de operação');
    }

    const data: ApiResponse<InvoiceTransactionTypeWithERP> = await response.json();
    return data.data!;
  }

  /**
   * Atualiza uma natureza de operação e seus mapeamentos ERP
   * PUT /api/invoice-transaction-types/:id
   */
  async update(
    id: number,
    formData: InvoiceTransactionTypeFormData,
  ): Promise<InvoiceTransactionTypeWithERP> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/invoice-transaction-types/${id}`, formData);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Falha ao atualizar natureza de operação');
    }

    const data: ApiResponse<InvoiceTransactionTypeWithERP> = await response.json();
    return data.data!;
  }

  /**
   * Exclui uma natureza de operação
   * DELETE /api/invoice-transaction-types/:id
   */
  async delete(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/invoice-transaction-types/${id}`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Falha ao excluir natureza de operação');
    }
  }

  /**
   * Lista naturezas de operação filtradas por empresa (somente as que possuem mapeamento ERP).
   * Retorna todas (ativas e inativas). Usado na tela de configuração/listagem.
   * GET /api/invoice-transaction-types?company_id=X
   */
  async getByCompany(companyId: number): Promise<InvoiceTransactionType[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/invoice-transaction-types?company_id=${companyId}&page=1&page_size=100`,
    );

    if (!response.ok) {
      throw new Error('Falha ao buscar naturezas de operação para a empresa');
    }

    const json = await response.json();
    return json.data || [];
  }

  /**
   * Lista naturezas de operação **ativas** filtradas por empresa e escopo.
   * Endpoint dedicado para uso nos formulários de criação de nota fiscal,
   * garantindo que somente naturezas ativas sejam exibidas.
   *
   * GET /api/invoice-transaction-types/active?company_id=X[&scope=sales|supply|gift]
   *
   * @param companyId - ID da empresa
   * @param scope - 'sales' (final_customer=true, transaction_type=S, is_billable=true),
   *                'gift'  (final_customer=true, transaction_type=S, is_billable=false),
   *                'supply' (critério composto de suprimentos),
   *                ou undefined (todas ativas da empresa)
   */
  async getActiveByCompany(
    companyId: number,
    scope?: 'sales' | 'supply' | 'gift',
  ): Promise<InvoiceTransactionType[]> {
    const params = new URLSearchParams({
      company_id: companyId.toString(),
    });
    if (scope) params.set('scope', scope);

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/invoice-transaction-types/active?${params}`,
    );

    if (!response.ok) {
      throw new Error('Falha ao buscar naturezas de operação ativas');
    }

    const json = await response.json();
    return json.data || [];
  }

  /**
   * Lista naturezas de operação filtradas por empresa, transaction_type e final_customer.
   * Filtra localmente os resultados da API até que o backend suporte query params dedicados.
   */
  async getByCompanyFiltered(
    companyId: number,
    filter: {
      transaction_type?: ('E' | 'S')[];
      final_customer?: boolean;
    },
  ): Promise<InvoiceTransactionType[]> {
    const all = await this.getByCompany(companyId);
    return all.filter(t => {
      if (filter.transaction_type && filter.transaction_type.length > 0) {
        if (!t.transaction_type || !filter.transaction_type.includes(t.transaction_type)) {
          return false;
        }
      }
      if (filter.final_customer !== undefined) {
        if (t.final_customer !== filter.final_customer) {
          return false;
        }
      }
      return true;
    });
  }

  /**
   * Lista naturezas de operação para o módulo de Suprimentos.
   * Critério: (transaction_type IN ('E','S') AND final_customer=false)
   *        OR (final_customer=true AND transaction_type='E')
   */
  async getByCompanySupply(companyId: number): Promise<InvoiceTransactionType[]> {
    const all = await this.getByCompany(companyId);
    return all.filter(t => {
      if (!t.transaction_type) return false;
      // Entrada ou Saída com final_customer=false (revenda, transferência, etc.)
      if ((t.transaction_type === 'E' || t.transaction_type === 'S') && t.final_customer === false) {
        return true;
      }
      // Entrada com final_customer=true (devolução de venda ao consumidor)
      if (t.transaction_type === 'E' && t.final_customer === true) {
        return true;
      }
      return false;
    });
  }

  /**
   * Lista todas as naturezas de operação ativas para o contexto de Vendas.
   * Critério: TransactionType='S', FinalCustomer=true, Active=true
   * GET /api/invoice-transaction-types/active-list?scope=sales
   */
  async getActiveSales(): Promise<InvoiceTransactionType[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/invoice-transaction-types/active-list?transaction_type=S&final_customer=true`,
    );

    if (!response.ok) {
      throw new Error('Falha ao buscar naturezas de operação ativas para vendas');
    }

    const json = await response.json();
    return json.data || [];
  }

  /**
   * Lista todas as naturezas de operação ativas para o contexto de Suprimentos.
   * Critério: TransactionType='E', Active=true
   * GET /api/invoice-transaction-types/active-list?transaction_type=E
   */
  async getActiveSupply(): Promise<InvoiceTransactionType[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/invoice-transaction-types/active-list?transaction_type=E`,
    );

    if (!response.ok) {
      throw new Error('Falha ao buscar naturezas de operação ativas para suprimentos');
    }

    const json = await response.json();
    return json.data || [];
  }
}

export const invoiceTransactionTypeService = new InvoiceTransactionTypeService();
export default invoiceTransactionTypeService;
